import { defineElement, defineSvgElement } from "domvm";
import { Editor } from "./Editor.js";

// SVG - source: https://codepen.io/JMChristensen/pen/AGbeEy
// Two half-circles - source: https://stackoverflow.com/questions/14222138/css-progress-circle
// Four segments - source: http://www.independent-software.com/drawing-progress-arc-in-pure-css-using-skewed-rectangles.html

export class CircularSlider extends Editor {

    constructor(parent, params = {}) {

        params = Object.assign({
            value: [0, 100],
            onValuChange: null,
            // format value for input control
            onFormatValue: null,
            // parse value in input control
            onParseValue: null,
            min: 0,
            max: 100,
            step: 5,
            radius: 70,
            orientation: 'bottom',
            readOnly: false,
            autofocus: false,
            // Flag: true = editor and dropdown gauge is rendered; false = standalone gauge is rendered.
            inline: false,
        }, params || {});

        super(parent, params);

        //
        // Internal flags 
        //
        // Expansion of gauge dropdown. Used when inline = false.
        this.expanded = false;


    }

    // --------------------------------------------------------------------------------------------

    isAutofocus() {
        return (typeof this.autofocus === 'function') ? this.autofocus.call(this) : this.autofocus;
    }

    getMin() {
        return (typeof this.min == 'function') ? this.min.call(this) : this.min;
    }

    getMax() {
        return (typeof this.max == 'function') ? this.max.call(this) : this.max;
    }

    getStep() {
        return (typeof this.step == 'function') ? this.step.call(this) : this.step;
    }

    getOrientation() {
        return (typeof this.orientation == 'function') ? this.orientation.call(this) : this.orientation;
    }

    getRadius() {
        return (typeof this.radius == 'function') ? this.radius.call(this) : this.radius;
    }

    isInline() {
        return (typeof this.inline == 'function') ? this.inline.call(this) : this.inline;
    }

    isExpanded() {
        return this.expanded;
    }

    // --------------------------------------------------------------------------------------------

    getStart() {
        let v = this.getValue();
        if (Array.isArray(v))
            return v[0];
        return v;
    }

    getEnd() {
        let v = this.getValue();
        if (Array.isArray(v))
            return v[1];
        return v;
    }

    // --------------------------------------------------------------------------------------------

    changeValue(value, forceChange, omitRedraw, omitRefresh) {

        let step = this.getStep();

        if (step) {
            if (Array.isArray(value)) {
                value = [roundValue(value[0]), roundValue(value[1])];
            } else {
                value = roundValue(value);
            }
        }

        super.changeValue(value, forceChange, omitRedraw, omitRefresh);

        function roundValue(v) {
            return Math.round(v / step) * step
        };

    }

    // --------------------------------------------------------------------------------------------    

    togglePicker() {

        if (this.isInline()) return;
        this.expanded = !this.expanded;
        this.invalidate();
        if (this.expanded && this.$editor) this.$editor.focus();

    }

    closePicker() {

        if (this.isInline()) return;
        this.expanded = false;
        this.invalidate();

    }

    // --------------------------------------------------------------------------------------------    

    invalidate() {

        this.getParent().invalidate();

    }

    _render(r, vm) {

        const renderer = r;
        return renderControl(vm, this, renderer);

    }

}


function renderControl(vm, control, renderer) {


    if (control.isInline())
        return renderGauge(vm, control);
    return renderEditor(vm, control, renderer);

}

const el = defineElement;
const sv = defineSvgElement;

function renderEditor(vm, control, renderer) {

    let isReadOnly = (control.isReadOnly() && 'readonly') || null,
        isDisabled = (control.isDisabled() && 'disabled') || null,
        isReadOnlyOrDisabled = ((control.isReadOnly() || control.isDisabled()) && 'disabled') || null,
        value = control.getValue(),
        formattedValue = (typeof control.onFormatValue == 'function') ? control.onFormatValue(value) : value;

    return renderer.el('div', 'form-autocomplete', null, [
        renderer.el('div', 'input-group', null, [
            renderer.el('input', 'form-input', {
                type: 'text',
                value: formattedValue,
                readonly: isReadOnly,
                disabled: isDisabled,
                onblur: function (e) { control.closePicker(); },
                onchange: onValueChange,
                'data-focus': (!(isDisabled || isReadOnly) && control.isAutofocus()) ? 'auto' : null,
                _hooks: {
                    didInsert: function (node) {
                        control.$editor = node.el;
                    },
                    willRemove: function (node) {
                        control.$editor = null;
                    }
                }
            }),
            renderer.button(null, 'tachometer-alt', {
                css: 'input-group-btn',
                props: {
                    disabled: isReadOnlyOrDisabled,
                    onclick: function (e) { control.togglePicker(); }
                }
            }),
        ]),
        (control.isExpanded() ? renderGauge(vm, control) : null)
    ]);

    function onValueChange(e) {

        e.preventDefault();

        let text = e.target.value,
            value = (typeof control.onParseValue == 'function') ? control.onParseValue(text) : text;

        if (typeof control.onValueChange == 'function')
            control.onValueChange(value);

    };

};

const PI = Math.PI,
    PI_HALF = PI / 2.0,
    PI_DOUBLE = PI * 2.0

function renderGauge(vm, control) {

    var readOnly = control.isReadOnly(),
        movingButtonInfo = null,
        value = control.getValue(),
        rangeMode = Array.isArray(value),
        startValue = control.getStart() || 0,
        endValue = control.getEnd() || 0,
        minValue = control.getMin(),
        maxValue = control.getMax(),
        range = Math.abs(maxValue - minValue),
        nodeStyles = window.getComputedStyle(control.$editor),
        nodeHeight = 200,
        nodeWidth = (control.$editor && (
            control.$editor.clientWidth
            + parseFloat(nodeStyles.paddingLeft) + parseFloat(nodeStyles.paddingRight)
            + parseFloat(nodeStyles.borderLeftWidth) + parseFloat(nodeStyles.borderRightWidth)
        )) || 200,
        centerX = (nodeWidth / 2),
        centerY = 100,
        dist = (endValue < startValue ? endValue + range : endValue) - startValue,
        arcClockwise = 1,
        arcLarge = dist > (range / 2.0) ? 1 : 0,
        startOffset;

    switch (control.getOrientation()) {
        case 'top':
            startOffset = -PI_HALF;
            break;
        case 'left':
            startOffset = PI;
            break;
        case 'right':
            startOffset = 0;
            break;
        default:
            startOffset = PI_HALF;
            break;
    }

    let r = control.getRadius(),
        start = valueToPoint(startValue),
        end = valueToPoint(endValue),
        gaugeNode, valuesNode;

    if (rangeMode) {

        if (dist == range) {

            gaugeNode = sv('svg', { width: nodeWidth, height: nodeHeight, viewPort: '0 0 100 100', version: '1.1', xmlns: 'http://www.w3.org/2000/svg' }, [
                sv('circle', { class: 'back', r: r, cx: centerX, cy: centerY, fill: 'transparent' }),
                sv('circle', { class: 'bar', r: r, cx: centerX, cy: centerY, fill: 'transparent' }),
                (readOnly ? null : sv('circle', { class: 'circular-slider-btn start', r: 5, cx: start.x, cy: start.y, onmousedown: [onButtonMoveStart, 'start'], ontouchdown: [onButtonMoveStart, 'start'] })),
                (readOnly ? null : sv('circle', { class: 'circular-slider-btn end', r: 5, cx: end.x, cy: end.y, onmousedown: [onButtonMoveStart, 'end'], ontouchdown: [onButtonMoveStart, 'end'] }))
            ]);

        } else {

            gaugeNode = sv('svg', { width: nodeWidth, height: nodeHeight, viewPort: '0 0 100 100', version: '1.1', xmlns: 'http://www.w3.org/2000/svg' }, [
                sv('circle', { class: 'back', r: r, cx: centerX, cy: centerY, fill: 'transparent' }),
                sv('path', { class: 'bar', fill: 'transparent', d: `M ${start.x} ${start.y} A ${r} ${r}, 0, ${arcLarge}, ${arcClockwise}, ${end.x} ${end.y}` }),
                (readOnly ? null : sv('circle', { class: 'circular-slider-btn start', r: 5, cx: start.x, cy: start.y, onmousedown: [onButtonMoveStart, 'start'], ontouchdown: [onButtonMoveStart, 'start'] })),
                (readOnly ? null : sv('circle', { class: 'circular-slider-btn end', r: 5, cx: end.x, cy: end.y, onmousedown: [onButtonMoveStart, 'end'], ontouchdown: [onButtonMoveStart, 'end'] }))
            ]);

        }

        valuesNode = el('.text-center', [
            formatValue(startValue) + ' - ' + formatValue(endValue)
        ]);

    } else {

        gaugeNode = sv('svg', { width: nodeWidth, height: nodeHeight, viewPort: '0 0 100 100', version: '1.1', xmlns: 'http://www.w3.org/2000/svg' }, [
            sv('circle', { class: 'back', r: r, cx: centerX, cy: centerY, fill: 'transparent' }),
            (readOnly ? null : sv('circle', { class: 'circular-slider-btn start', r: 5, cx: start.x, cy: start.y, onmousedown: [onButtonMoveStart, 'start'], ontouchdown: [onButtonMoveStart, 'start'] })),
        ]);

        valuesNode = el('.text-center', [
            formatValue(startValue)
        ])

    }

    return el('.circular-slider' + (control.isInline() ? '' : '.menu'), {}, [
        gaugeNode,
        valuesNode,
    ]);

    function formatValue(val) {

        if (val === undefined || val === null || val === false)
            return '';
        if (typeof control.onFormatValue == 'function')
            return control.onFormatValue(val);
        return val.toString();

    };

    function valueToPoint(val) {

        let rad = (PI_DOUBLE * val / range) + startOffset;
        if (rad < 0)
            rad += PI_DOUBLE;
        //if (rad >= PI_DOUBLE)
        if (rad > PI_DOUBLE)
            rad -= PI_DOUBLE;
        return {
            x: centerX + Math.cos(rad) * r,
            y: centerY + Math.sin(rad) * r
        };

    };

    function pointToValue(x, y) {

        let rad;

        if (y == 0) {
            rad = (x < 0) ? PI : 0;
        } else {
            rad = Math.atan(y / x);
            if (x < 0)
                rad += PI;
        }

        rad -= startOffset;
        if (rad < 0)
            rad += PI_DOUBLE;
        //if (rad >= PI_DOUBLE)
        if (rad > PI_DOUBLE)
            rad -= PI_DOUBLE;

        let value = rad * range / PI_DOUBLE;

        return value;

    };

    function onButtonMoveStart(button, e, node) {

        e.preventDefault();

        var rect = node.el.parentElement.parentElement.getBoundingClientRect(),
            eventData = e;

        if (eventData.touches) {
            eventData = eventData.touches[0];
        }

        movingButtonInfo = {
            button: button,
            startX: rect.left + centerX,
            startY: rect.top + centerY,
        };

        document.addEventListener('mousemove', onButtonMoveProgress);
        document.addEventListener('mouseup', onButtonMoveEnd);
        document.addEventListener('touchmove', onButtonMoveProgress);
        document.addEventListener('touchend', onButtonMoveEnd);


    };

    function onButtonMoveProgress(e) {

        e.preventDefault();

        var eventData = e,
            actualValue = control.getValue() || [];

        if (eventData.touches) {
            eventData = eventData.touches[0];
        }

        let dx = eventData.pageX - movingButtonInfo.startX,
            dy = eventData.pageY - movingButtonInfo.startY;

        let newValue = pointToValue(dx, dy);

        if (rangeMode) {
            if (movingButtonInfo.button == 'end') {
                control.changeValue([actualValue[0], newValue]);
            } else {
                control.changeValue([newValue, actualValue[1]]);
            }
        } else {
            control.changeValue(newValue);
        }

    };

    function onButtonMoveEnd(e) {

        document.removeEventListener('mousemove', onButtonMoveProgress);
        document.removeEventListener('mouseup', onButtonMoveEnd);
        document.removeEventListener('touchmove', onButtonMoveProgress);
        document.removeEventListener('touchend', onButtonMoveEnd);

    };

};

